import {
  EyeOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PlusOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Breadcrumb,
  Button,
  Checkbox,
  Col,
  Dropdown,
  Input,
  Menu,
  message,
  Pagination,
  Popconfirm,
  Row,
  Space,
  Table,
  Tooltip,
} from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { debounce } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { CSVLink } from 'react-csv';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import ImportDataModal from '../../Common/ImportDataModal';
import logo from '../../Images/alux-logo.png';
import { ReactComponent as Archive } from '../../Images/archive.svg';
import { ReactComponent as Archiveicon } from '../../Images/Archiveicon.svg';
import { ReactComponent as Back } from '../../Images/Back.svg';
import { ReactComponent as ColumnList } from '../../Images/ColumnList.svg';
import { ReactComponent as EditIcon } from '../../Images/EditIcon.svg';
import formatFile from '../../Images/sample-files/customer-type-format.csv';
import customerTypeService from '../../services/customer-type.service';
import { createActivityLog } from '../../slices/activity-log';
import { fetchAndSetIpAddress } from '../../slices/ipSlice';
import AddEditCustomerType from './AddEditCustomerType';
import ViewCustomerType from './ViewCustomerType';
import eventBus from '../../utils/eventBus';

function CustomerTypeList() {
  const dispatch = useDispatch();
  const ipAddress = useSelector(state => state.ip.ipAddress);
  const [page, setPage] = useState(1);
  const [pagesize, setPageSize] = useState(20);
  const [orderBy, setOrderBy] = useState('');
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedCustomerType, setSelectedCustomerType] = useState(null);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [totalRecord, setTotalRecord] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);
  const [search, setSearch] = useState(
    localStorage.getItem('customer_type_search') || '',
  );
  const [inputValue, setInputValue] = useState(
    localStorage.getItem('customer_type_search') || '',
  );

  const fetchCustomerTypeList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await customerTypeService.getCustomerTypeList(
        page,
        pagesize,
        search || undefined,
        orderBy || undefined,
        undefined,
      );
      if (response.data) {
        const customerTypes =
          response.data.results?.data ||
          response.data.results ||
          response.data ||
          [];
        setData(Array.isArray(customerTypes) ? customerTypes : []);
        setTotalRecord(response.data.count || customerTypes.length || 0);
      }
    } catch (error) {
      message.error('Failed to fetch customer types');
      console.error('Error fetching customer types:', error);
    } finally {
      setLoading(false);
    }
  }, [page, pagesize, search, orderBy]);

  useEffect(() => {
    fetchCustomerTypeList();
  }, [fetchCustomerTypeList]);
  useEffect(() => {
    const handleReload = () => {
      fetchCustomerTypeList();
    };

    eventBus.on('financialYear:refresh', handleReload);

    return () => {
      eventBus.off('financialYear:refresh', handleReload);
    };
  }, [fetchCustomerTypeList]);

  useEffect(() => {
    if (!ipAddress) {
      dispatch(fetchAndSetIpAddress());
    }
  }, [dispatch, ipAddress]);

  const debouncedAPICall = useMemo(
    () =>
      debounce(value => {
        setSearch(value);
        setPage(1);
        localStorage.setItem('customer_type_search', value);
      }, 500),
    [],
  );

  const resetFilter = () => {
    setSearch('');
    setInputValue('');
    setOrderBy('');
    setPage(1);
    localStorage.removeItem('customer_type_search');
    fetchCustomerTypeList();
  };

  const handlePageSizeChange = (current, size) => {
    setPage(1);
    setPageSize(size);
  };

  const GetSortingEnumValue = useCallback((sortField, sortOrder) => {
    if (!sortField || !sortOrder) return 'id';
    const prefix = sortOrder === 'descend' ? '-' : '';
    return `${prefix}${sortField}`;
  }, []);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    if (sorter?.columnKey && sorter.order) {
      const orderBy = GetSortingEnumValue(sorter.columnKey, sorter.order);
      setOrderBy(orderBy);
    }
    if (pagination?.pageSize) setPageSize(pagination.pageSize);
    if (pagination?.current) setPage(pagination.current);
  };

  const handleSearch = e => {
    const value = e.target.value;
    debouncedAPICall(value);
    setInputValue(value);
  };

  const handleEdit = record => {
    setSelectedCustomerType(record);
    setOpen(true);
  };

  const handleView = async record => {
    try {
      setLoading(true);
      const response = await customerTypeService.getCustomerTypeById(record.id);
      if (response.data) {
        // Backend may wrap the actual object inside `data`
        const customerTypeData =
          response.data.data || response.data.results || response.data;
        setSelectedCustomerType(customerTypeData);
        setViewOpen(true);
      }
    } catch (error) {
      message.error('Failed to fetch customer type details');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async id => {
    try {
      await customerTypeService.deleteCustomerType(id);
      message.success('Customer type archived successfully');
      setSelectedRowKeys([]);
      fetchCustomerTypeList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to archive customer type';
      message.error(errorMessage);
    }
  };

  const handleBulkArchive = async () => {
    if (!selectedRowKeys || selectedRowKeys.length === 0) {
      message.error('Please select at least one record to archive');
      return;
    }

    try {
      await customerTypeService.bulkArchiveCustomerType(selectedRowKeys);
      message.success(
        `${selectedRowKeys.length} Customer type(s) archived successfully`,
      );
      setSelectedRowKeys([]);
      fetchCustomerTypeList();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || 'Failed to archive customer types';
      message.error(errorMessage);
    }
  };

  const onClose = useCallback(() => {
    setOpen(false);
    setSelectedCustomerType(null);
    fetchCustomerTypeList();
  }, [fetchCustomerTypeList]);

  const onViewClose = useCallback(() => {
    setViewOpen(false);
    setSelectedCustomerType(null);
  }, []);

  const refreshTable = useCallback(() => {
    fetchCustomerTypeList();
  }, [fetchCustomerTypeList]);

  const hasPermission = useCallback(
    permission => {
      if (!currentUser?.permissions?.permissions) return false;
      const allPermissions = Object.values(
        currentUser.permissions.permissions,
      ).flat();
      return allPermissions.includes(permission);
    },
    [currentUser],
  );

  const canView = useMemo(() => hasPermission('view_customertype'), [hasPermission]);
  const canAdd = useMemo(() => hasPermission('add_customertype'), [hasPermission]);
  const canEdit = useMemo(() => hasPermission('change_customertype'), [hasPermission]);
  const canDelete = useMemo(
    () => hasPermission('delete_customertype'),
    [hasPermission],
  );
  const canDownloadExcel = useMemo(
    () => hasPermission('download_customer_type_excel_copy'),
    [hasPermission],
  );
  const canDownloadPdf = useMemo(
    () => hasPermission('download_customer_type_pdf_copy'),
    [hasPermission],
  );

  const dataSource = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        key: item.id || index,
        SrNo: (page - 1) * pagesize + index + 1,
      })),
    [data, page, pagesize],
  );

  const allColumns = useMemo(
    () => [
      {
        title: 'Sr. No.',
        dataIndex: 'SrNo',
        key: 'SrNo',
        sorter: false,
        width: 80,
      },
      {
        title: 'Customer Type',
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        width: 200,
      },
      {
        title: 'Created At',
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 150,
        render: (text, record) => {
          return (
            <span>
              {record?.created_at
                ? dayjs(record.created_at).format('DD-MM-YYYY HH:mm')
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Created By',
        dataIndex: 'created_by',
        key: 'created_by',
        sorter: true,
        width: 150,
        render: (text, record) => {
          const createdBy = record?.created_by;
          if (createdBy && typeof createdBy === 'object') {
            return (
              <span>
                {createdBy.first_name || ''} {createdBy.last_name || ''}
              </span>
            );
          }
          // Handle case where created_by might be a string or other format
          if (typeof createdBy === 'string' && createdBy.trim()) {
            return <span>{createdBy}</span>;
          }
          return <span>N/A</span>;
        },
      },
      {
        title: 'Updated At',
        dataIndex: 'updated_at',
        key: 'updated_at',
        sorter: true,
        width: 150,
        render: (text, record) => {
          return (
            <span>
              {record?.updated_at
                ? dayjs(record.updated_at).format('DD-MM-YYYY HH:mm')
                : 'N/A'}
            </span>
          );
        },
      },
      {
        title: 'Updated By',
        dataIndex: 'updated_by',
        key: 'updated_by',
        sorter: true,
        width: 150,
        render: (text, record) => {
          const updatedBy = record?.updated_by;
          if (updatedBy && typeof updatedBy === 'object') {
            return (
              <span>
                {updatedBy.first_name || ''} {updatedBy.last_name || ''}
              </span>
            );
          }
          // Handle case where updated_by might be a string or other format
          if (typeof updatedBy === 'string' && updatedBy.trim()) {
            return <span>{updatedBy}</span>;
          }
          return <span>N/A</span>;
        },
      },
      {
        title: 'Action',
        dataIndex: '',
        key: 'action',
        fixed: 'right',
        width: 200,
        render: (text, record) => (
          <Space>
            {canView && (
              <Tooltip title="View">
                <span
                  onClick={() => handleView(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EyeOutlined style={{ fontSize: '17px' }} />
                </span>
              </Tooltip>
            )}
            {canEdit && (
              <Tooltip title="Edit">
                <span
                  onClick={() => handleEdit(record)}
                  style={{ cursor: 'pointer' }}
                >
                  <EditIcon />
                </span>
              </Tooltip>
            )}
            {canDelete && (
              <Popconfirm
                title="Are you sure you want to archive this customer type?"
                onConfirm={() => handleDelete(record.id)}
                onCancel={() => message.info('Action cancelled')}
                okText="Yes"
                cancelText="No"
              >
                <Tooltip placement="top" title={'Archive'}>
                  <span style={{ cursor: 'pointer', display: 'inline-block' }}>
                    <Archive />
                  </span>
                </Tooltip>
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ],
    [handleEdit, handleView, handleDelete, canView, canEdit, canDelete],
  );

  const [checkedList, setCheckedList] = useState([
    'SrNo',
    'name',
    'created_at',
    'created_by',
    'updated_at',
    'updated_by',
    'action',
  ]);

  const handleColumnVisibilityChange = useCallback(checkedValues => {
    setCheckedList(checkedValues);
  }, []);

  const filteredColumns = useMemo(
    () => allColumns.filter(column => checkedList.includes(column.key)),
    [allColumns, checkedList],
  );

  const columnMenu = useMemo(
    () => (
      <Menu className="packagelistdropdown">
        <Menu.Item key="columnMenu">
          <Checkbox.Group
            value={checkedList}
            onChange={handleColumnVisibilityChange}
            onClick={e => e.stopPropagation()}
          >
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Checkbox value="SrNo" disabled>
                  Sr. No.
                </Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="name">Customer Type</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="created_at">Created At</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="created_by">Created By</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="updated_at">Updated At</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="updated_by">Updated By</Checkbox>
              </Col>
              <Col xs={24} md={12}>
                <Checkbox value="action" disabled>
                  Action
                </Checkbox>
              </Col>
            </Row>
          </Checkbox.Group>
        </Menu.Item>
      </Menu>
    ),
    [checkedList, handleColumnVisibilityChange],
  );

  const csvheaders = [
    { label: 'Sr. No.', key: 'SrNo' },
    { label: 'Customer Type', key: 'name' },
    { label: 'Created At', key: 'created_at' },
    { label: 'Created By', key: 'created_by' },
    { label: 'Updated At', key: 'updated_at' },
    { label: 'Updated By', key: 'updated_by' },
  ];

  const csvFilterheaders = csvheaders.filter(column =>
    checkedList.includes(column.key),
  );

  const csvListData = data.map((item, index) => ({
    SrNo: (page - 1) * pagesize + index + 1,
    name: item.name || 'N/A',
    created_at: item.created_at
      ? moment(item.created_at).format('DD-MM-YYYY')
      : 'N/A',
    created_by: (() => {
      const createdBy = item.created_by;
      if (createdBy && typeof createdBy === 'object') {
        return `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim() || 'N/A';
      }
      if (typeof createdBy === 'string' && createdBy.trim()) {
        return createdBy;
      }
      return 'N/A';
    })(),
    updated_at: item.updated_at
      ? moment(item.updated_at).format('DD-MM-YYYY')
      : 'N/A',
    updated_by: (() => {
      const updatedBy = item.updated_by;
      if (updatedBy && typeof updatedBy === 'object') {
        return `${updatedBy.first_name || ''} ${updatedBy.last_name || ''}`.trim() || 'N/A';
      }
      if (typeof updatedBy === 'string' && updatedBy.trim()) {
        return updatedBy;
      }
      return 'N/A';
    })(),
  }));

  const handlePdfDownload = () => {
    const pdf = new jsPDF('p', 'pt', 'a4');
    const pdfColumns = [
      { header: 'Sr No.', dataKey: 'SrNo' },
      { header: 'Customer Type', dataKey: 'name' },
      { header: 'Created At', dataKey: 'created_at' },
      { header: 'Created By', dataKey: 'created_by' },
      { header: 'Updated At', dataKey: 'updated_at' },
      { header: 'Updated By', dataKey: 'updated_by' },
    
    ];

    const pdffilteredColumns = pdfColumns.filter(column =>
      checkedList.includes(column.dataKey),
    );

    const rows = data.map((item, index) => ({
      SrNo: (page - 1) * pagesize + index + 1,
      name: item.name || 'N/A',
      created_at: item.created_at
        ? moment(item.created_at).format('DD-MM-YYYY')
        : 'N/A',
      created_by: (() => {
        const createdBy = item.created_by;
        if (createdBy && typeof createdBy === 'object') {
          return `${createdBy.first_name || ''} ${createdBy.last_name || ''}`.trim() || 'N/A';
        }
        if (typeof createdBy === 'string' && createdBy.trim()) {
          return createdBy;
        }
        return 'N/A';
      })(),
      updated_at: item.updated_at
        ? moment(item.updated_at).format('DD-MM-YYYY')
        : 'N/A',
      updated_by: (() => {
        const updatedBy = item.updated_by;
        if (updatedBy && typeof updatedBy === 'object') {
          return `${updatedBy.first_name || ''} ${updatedBy.last_name || ''}`.trim() || 'N/A';
        }
        if (typeof updatedBy === 'string' && updatedBy.trim()) {
          return updatedBy;
        }
        return 'N/A';
      })(),
    }));

    const pageWidth = pdf.internal.pageSize.width;
    const pageHeight = pdf.internal.pageSize.height;

    // Heading
    pdf.setFont('times', 'bold');
    pdf.setFontSize(16);
    const headingText = 'Customer Type List';
    const headingX = pageWidth / 2 - pdf.getTextWidth(headingText) / 2;
    pdf.text(headingText, headingX, 40);

    pdf.addImage(logo, 'PNG', pageWidth - 100, 10, 90, 30);

    pdf.autoTable({
      columns: pdffilteredColumns,
      body: rows,
      startY: 65,
      theme: 'grid',
      styles: {
        font: 'times',
        halign: 'center',
        overflow: 'linebreak',
        cellPadding: 3.5,
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        textColor: [0, 0, 0],
      },
      headStyles: {
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
        fillColor: false,
      },
      rowStyles: {
        lineWidth: 0.5,
        lineColor: [0, 0, 0],
      },
      tableLineColor: [0, 0, 0],
      rowPageBreak: 'avoid',
      didDrawPage: (hookData) => {
        const pageCount = pdf.internal.getNumberOfPages();
        const currentPage = hookData.pageNumber;

        const dateStr = moment().format('DD-MM-YYYY HH:mm');
        pdf.setFontSize(10);
        pdf.text(dateStr, 40, pageHeight - 20);

        pdf.text(
          `Page ${currentPage} of ${pageCount}`,
          pageWidth - 100,
          pageHeight - 20,
        );
      },
    });

    pdf.save(`customer_type_${new Date().toISOString().split('T')[0]}.pdf`);

    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Customer Type',
        ip_address: ipAddress,
        discription: 'Customer type pdf download successfully!',
      }),
    );
  };

  const activityLog = async () => {
    dispatch(
      createActivityLog({
        action: 'DOWNLOAD',
        module_name: 'Customer Type',
        ip_address: ipAddress,
        discription: 'Customer type excel download successfully!',
      }),
    );
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  if (!canView) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Access Denied</h2>
        <p>You don't have permission to view this page.</p>
      </div>
    );
  }

  return (
    <>
      <div className="sticyaction">
        <Row className="pagenamerow mb-0" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>Customer Type Master</h2>
            <div className="bredcrumbwrp">
              <Link to="/dashboard" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: 'Customer Type Master' },
                ]}
              />
            </div>
          </Col>
          <Col>
            <Space>
             
              {canAdd && (
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setSelectedCustomerType(null);
                    setOpen(true);
                  }}
                >
                  Add Customer Type
                </Button>
              )}
              {canAdd && (
                <Button
                  type="primary"
                  icon={<UploadOutlined />}
                  onClick={() => setImportModalOpen(true)}
                >
                  Bulk Import
                </Button>
              )}
              {selectedRowKeys.length > 0 && canDelete && (
                <Popconfirm
                  title={`Are you sure you want to archive ${selectedRowKeys.length} selected customer type(s)?`}
                  onConfirm={handleBulkArchive}
                  okText="Yes"
                  cancelText="No"
                >
                  <Button type="primary" danger>
                    Bulk Archive ({selectedRowKeys.length})
                  </Button>
                </Popconfirm>
              )}
              <Link to="/archive-customer-type-list" className="iconlink">
                <Tooltip title="Archive">
                  <Archiveicon />
                </Tooltip>
              </Link>
              {canDownloadExcel && (
                <Tooltip placement="top" title={'Export Excel'}>
                  <Button
                    type="primary"
                    className="iconlink"
                    style={{ width: '10px' }}
                    onClick={activityLog}
                  >
                    <CSVLink
                      filename={`customer_type_${new Date().toISOString().split('T')[0]}.csv`}
                      data={csvListData}
                      className="iconlink"
                      headers={csvFilterheaders}
                    >
                      <FileExcelOutlined />
                    </CSVLink>
                  </Button>
                </Tooltip>
              )}
              {canDownloadPdf && (
                <Tooltip placement="top" title={'Export PDF'}>
                  <Button
                    type="primary"
                    className="iconlink"
                    onClick={handlePdfDownload}
                  >
                    <FilePdfOutlined />
                  </Button>
                </Tooltip>
              )}
              <div className="gridiconswrap">
                <Tooltip placement="topLeft" title="Column Filter Options">
                  <Dropdown
                    popupRender={() => columnMenu}
                    trigger={['click']}
                    placement="bottomRight"
                    open={dropdownVisible}
                    onOpenChange={setDropdownVisible}
                  >
                    <Button
                      type="primary"
                      icon={<ColumnList />}
                      onClick={e => e.preventDefault()}
                    />
                  </Dropdown>
                </Tooltip>
              </div>
            </Space>
          </Col>
        </Row>
      </div>

      <div className="formwrapper">
        <Row gutter={16} className="mb-3">
          <Col xs={24} sm={12} md={8} lg={6}>
            <Input
              placeholder="Search by name"
              prefix={<SearchOutlined />}
              value={inputValue}
              onChange={handleSearch}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={8} lg={6}>
            <Button onClick={resetFilter}>Reset</Button>
          </Col>
        </Row>
      </div>
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ flex: 1, overflow: 'auto' }}>
          <div className="tablewrp">
            <Table
              rowKey="id"
              columns={filteredColumns}
              dataSource={dataSource}
              pagination={false}
              loading={loading}
              onChange={handleTableChange}
              rowSelection={canDelete ? rowSelection : null}
              scroll={{ x: 'max-content' }}
              locale={{
                emptyText: 'No customer types found',
              }}
            />
          </div>
        </div>
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            zIndex: 1000,
            background: '#fff',
            padding: 10,
            display: 'flex',
            justifyContent: 'flex-end',
          }}
        >
          <Pagination
            current={page}
            pageSize={pagesize}
            total={totalRecord}
            showSizeChanger
            onShowSizeChange={handlePageSizeChange}
            pageSizeOptions={['20', '50', '100']}
            showTotal={total => `Total ${total} items`}
            onChange={handlePageChange}
            locale={{ items_per_page: 'Items / Page' }}
          />
        </div>
      </div>

      {open && (
        <AddEditCustomerType
          open={open}
          onClose={onClose}
          customerType={selectedCustomerType}
        />
      )}

      {viewOpen && (
        <ViewCustomerType
          open={viewOpen}
          onClose={onViewClose}
          customerType={selectedCustomerType}
        />
      )}

      <ImportDataModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        refreshTable={refreshTable}
        formatFilePath={formatFile}
        model="customer-type"
      />
    </>
  );
}

export default CustomerTypeList;
